import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowRight, Clock, History } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { PageHero } from "@/components/shared";
import client, { imgUrl } from "@/lib/api";
import { eventsI18n, catLabel } from "@/i18n/events";
import { IMAGES } from "@/data/treasures";

const ALL = "all";

const formatDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

const EventCard = ({ ev, lang, te }) => (
  <Link
    to={`/neuigkeiten/${ev.id}`}
    data-testid={`event-card-${ev.id}`}
    className="cursor-link group relative rounded-2xl overflow-hidden bg-espresso text-left flex flex-col"
  >
    <div className="aspect-[16/10] overflow-hidden bg-espresso-deep">
      {ev.cover_image ? (
        <img src={imgUrl(ev.cover_image)} alt={ev.title} loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[1300ms] group-hover:scale-110" />
      ) : (
        <div className="w-full h-full grain-overlay" />
      )}
      <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase bg-gold text-espresso-deep px-3 py-1 rounded-full font-medium">
        {catLabel(ev.category, lang)}
      </span>
      {ev.archived && (
        <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase bg-espresso-deep/80 text-cream/80 px-3 py-1 rounded-full">
          <History className="w-3 h-3" /> {te.archived}
        </span>
      )}
    </div>
    <div className="p-6 flex-1 flex flex-col">
      {ev.event_date && (
        <span className="inline-flex items-center gap-2 text-[11px] tracking-wide text-gold/80 mb-2">
          <Calendar className="w-3.5 h-3.5" /> {formatDate(ev.event_date, lang)}{ev.event_time ? ` · ${ev.event_time}` : ""}
        </span>
      )}
      <h3 className="font-serif text-xl text-cream leading-snug">{ev.title}</h3>
      {ev.short_desc && <p className="mt-2 text-sm text-cream/60 font-sans leading-relaxed line-clamp-3">{ev.short_desc}</p>}
      <span className="mt-4 inline-flex items-center gap-1.5 text-xs text-gold font-sans group-hover:gap-2.5 transition-all">
        {te.readMore} <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </div>
  </Link>
);

const EventsPage = () => {
  const { lang } = useLang();
  const te = eventsI18n[lang];
  const ref = useReveal([lang]);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState(ALL);
  const [showArchive, setShowArchive] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { archived: showArchive };
    if (filter !== ALL) params.category = filter;
    client
      .get("/events", { params })
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [filter, showArchive]);

  const visibleCats = useMemo(() => categories.map((c) => c.name), [categories]);

  return (
    <div ref={ref} data-testid="events-page">
      <PageHero image={IMAGES.brockenWide} eyebrow={te.eyebrow} title={te.title} lead={te.lead} />

      <section className="py-16 md:py-24 bg-sand grain-overlay min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* Upcoming / Archive toggle */}
          <div className="flex justify-center mb-8" data-testid="archive-toggle">
            <div className="inline-flex rounded-full bg-cream border border-border p-1">
              <button
                onClick={() => setShowArchive(false)}
                data-testid="toggle-upcoming"
                className={`inline-flex items-center gap-2 px-6 py-2 rounded-full text-sm font-sans tracking-wide transition-all duration-300 ${!showArchive ? "bg-espresso text-gold shadow-[0_0_18px_rgba(200,169,126,0.4)]" : "text-espresso/60 hover:text-espresso"}`}
              >
                <Clock className="w-3.5 h-3.5" /> {te.upcoming}
              </button>
              <button
                onClick={() => setShowArchive(true)}
                data-testid="toggle-archive"
                className={`inline-flex items-center gap-2 px-6 py-2 rounded-full text-sm font-sans tracking-wide transition-all duration-300 ${showArchive ? "bg-espresso text-gold shadow-[0_0_18px_rgba(200,169,126,0.4)]" : "text-espresso/60 hover:text-espresso"}`}
              >
                <History className="w-3.5 h-3.5" /> {te.archive}
              </button>
            </div>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="event-filters">
            <button onClick={() => setFilter(ALL)} data-testid="filter-all"
              className={`px-5 py-2.5 rounded-full text-sm font-sans tracking-wide transition-all duration-300 ${filter === ALL ? "bg-gold text-espresso-deep shadow-[0_0_18px_rgba(200,169,126,0.5)]" : "bg-cream border border-border text-espresso/70 hover:border-gold hover:text-espresso"}`}>
              {te.all}
            </button>
            {visibleCats.map((name) => (
              <button key={name} onClick={() => setFilter(name)} data-testid={`filter-${name}`}
                className={`px-5 py-2.5 rounded-full text-sm font-sans tracking-wide transition-all duration-300 ${filter === name ? "bg-gold text-espresso-deep shadow-[0_0_18px_rgba(200,169,126,0.5)]" : "bg-cream border border-border text-espresso/70 hover:border-gold hover:text-espresso"}`}>
                {catLabel(name, lang)}
              </button>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="text-center text-espresso/50 py-20 font-sans">…</div>
          ) : events.length === 0 ? (
            <p className="text-center text-espresso/55 py-20 font-sans max-w-md mx-auto" data-testid="events-empty">{te.empty}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
              {events.map((ev) => (
                <EventCard key={ev.id} ev={ev} lang={lang} te={te} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsPage;
